import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { TaskService } from "@/services/taskService";
import type {
  Task,
  CreateTaskForm,
  TemplateStep,
  LookupValue,
  TaskAssigneeType,
  TemplateTask,
} from "@/types/task";

// Query keys for tasks
export const taskKeys = {
  all: ["tasks"] as const,
  userTasks: ["tasks", "user"] as const,
  byId: (id: string) => ["tasks", id] as const,
  templates: ["templates"] as const,
  templateById: (id: string) => ["templates", id] as const,
  lookupValues: (type: string) => ["lookupValues", type] as const,
};

// Get user tasks
export function useUserTasks() {
  return useQuery({
    queryKey: taskKeys.userTasks,
    queryFn: TaskService.getUserTasks,
  });
}

// Get single task
export function useTask(taskId: string) {
  return useQuery({
    queryKey: taskKeys.byId(taskId),
    queryFn: () => TaskService.getTask(taskId),
    enabled: !!taskId,
  });
}

// Get templates
export function useTemplates() {
  return useQuery<TemplateTask[]>({
    queryKey: taskKeys.templates,
    queryFn: TaskService.getTemplates,
  });
}

// Get templates by layer
export function useTemplatesByLayer(layerName: string) {
  return useQuery<TemplateTask[]>({
    queryKey: [...taskKeys.templates, "layer", layerName] as const,
    queryFn: () => TaskService.getTemplatesByLayer(layerName),
    enabled: !!layerName,
  });
}

// Get lookup values
export function useLookupValues(
  type: "category" | "task_status" | "step_status" | "priority"
) {
  return useQuery<LookupValue[]>({
    queryKey: taskKeys.lookupValues(type),
    queryFn: () => TaskService.getLookupValues(type),
  });
}

// Create task
export function useCreateTask() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (taskData: CreateTaskForm) => TaskService.createTask(taskData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: taskKeys.userTasks });
    },
  });
}

// Update task
export function useUpdateTask() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      taskId,
      taskData,
    }: {
      taskId: string;
      taskData: Partial<Task>;
    }) => TaskService.updateTask(taskId, taskData),
    onSuccess: (updatedTask) => {
      queryClient.invalidateQueries({ queryKey: taskKeys.userTasks });
      if (updatedTask) {
        queryClient.setQueryData(taskKeys.byId(updatedTask.id), updatedTask);
      }
    },
  });
}

// Delete task
export function useDeleteTask() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (taskId: string) => TaskService.deleteTask(taskId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: taskKeys.userTasks });
    },
  });
}

// Create task from template
export function useCreateTaskFromTemplate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      templateId,
      overrides,
    }: {
      templateId: string;
      overrides: CreateTaskForm;
    }) => TaskService.createTaskFromTemplate(templateId, overrides),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: taskKeys.userTasks });
    },
  });
}

// Create template
export function useCreateTemplate() {
  const queryClient = useQueryClient();

  return useMutation<
    TemplateTask,
    Error,
    {
      templateData: {
        name: string;
        description?: string;
        layer_name: string;
        category_id?: string;
        priority_id?: string;
        status_id?: string;
        default_assignee_type?: TaskAssigneeType;
        default_assignee?: string;
        default_due_days?: number;
        default_start_days?: number;
        auto_assign_steps?: boolean;
        require_photos?: boolean;
        require_geometry?: boolean;
        allowed_geometry_types?: ("Point" | "LineString" | "Polygon")[];
      };
      templateSteps: TemplateStep[];
    }
  >({
    mutationFn: ({ templateData, templateSteps }) =>
      TaskService.createTemplate(templateData, templateSteps),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: taskKeys.templates });
    },
  });
}

// Update template
export function useUpdateTemplate() {
  const queryClient = useQueryClient();

  return useMutation<
    TemplateTask,
    Error,
    {
      templateId: string;
      templateData: {
        name: string;
        description?: string;
        layer_name: string;
        category_id?: string;
        priority_id?: string;
        status_id?: string;
        default_assignee_type?: TaskAssigneeType;
        default_assignee?: string;
        default_due_days?: number;
        default_start_days?: number;
        auto_assign_steps?: boolean;
        require_photos?: boolean;
        require_geometry?: boolean;
        allowed_geometry_types?: ("Point" | "LineString" | "Polygon")[];
      };
      templateSteps: TemplateStep[];
    }
  >({
    mutationFn: ({ templateId, templateData, templateSteps }) =>
      TaskService.updateTemplate(templateId, templateData, templateSteps),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: taskKeys.templates });
    },
  });
}

// Delete template
export function useDeleteTemplate() {
  const queryClient = useQueryClient();

  return useMutation<void, Error, string>({
    mutationFn: (templateId: string) => TaskService.deleteTemplate(templateId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: taskKeys.templates });
    },
  });
}
